import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';

import { warehouseApi } from '../api/warehouse.api';
import { useServerErrors } from '../hooks/useServerErrors';
import { buildHubSnapshot } from '../snapshot';
import type { FieldError, OutboundOrder } from '../types';
import { useWarehouse } from '../WarehouseContext';
import DistanceField, {
  type DistanceDraft,
  distanceDraftOf,
  distanceError,
  typedDistance,
} from './DistanceField';
import OutboundDestinationStep, {
  type DestinationDraft,
  type DestinationField,
} from './OutboundDestinationStep';
import { fieldKeyOf } from './OutboundRequestFormModal';
import { FormField } from './form';
import { HubSelect } from './pickers';
import { parseQty } from './SkuLinesEditor';
import { metaCls } from './ui';

/**
 * "Ubah" outbound request: the create wizard's "Penerima & tujuan" step on
 * the order's current values, plus the hub the order is worked at — movable
 * only while OPEN (before picking; the server re-checks under lock).
 * Allowed until In delivery (status.ts `canEditOutbound`). A shipped order on a
 * Dash shipment still at the hub rewrites that shipment too (address,
 * coordinates, H3 — what Dispatch plans from); once a rider has it the
 * server answers 409 and nothing changes.
 */

interface Props {
  order: OutboundOrder | null;
  onClose: () => void;
  onSaved: (order: OutboundOrder) => void;
}

/** Every error belongs to the one step shown here. */
const SINGLE_STEP = () => 1;

const draftOf = (o: OutboundOrder): DestinationDraft => ({
  recipientName: o.recipientName ?? '',
  recipientPhone: o.recipientPhone ?? '',
  address: o.destinationAddress ?? '',
  latitude: String(o.destinationLatitude ?? ''),
  longitude: String(o.destinationLongitude ?? ''),
  destNotes: o.destinationNotes ?? '',
  // Prefilled, editable address fields (a B2B client can still pick a place).
  manual: true,
  matched: null,
  city:
    o.destinationCityID !== null && o.destinationCity
      ? { id: o.destinationCityID, name: o.destinationCity }
      : null,
  // The stored city stands until the address / point move (then the server
  // is asked again) or ops pick another one.
  cityTouched: false,
  cityWarning: null,
});

const sameDraft = (a: DestinationDraft, b: DestinationDraft) =>
  a.recipientName === b.recipientName &&
  a.recipientPhone === b.recipientPhone &&
  a.address === b.address &&
  a.latitude === b.latitude &&
  a.longitude === b.longitude &&
  a.destNotes === b.destNotes &&
  (a.city?.id ?? null) === (b.city?.id ?? null);

/** Required-input guard; the server's validator does the real check. */
const localErrors = (d: DestinationDraft): FieldError[] => {
  const out: FieldError[] = [];
  if (!d.recipientName.trim())
    out.push({ field: 'recipient.name', message: 'Nama penerima wajib diisi.' });
  if (!d.recipientPhone.trim())
    out.push({ field: 'recipient.phone', message: 'Telepon wajib diisi.' });
  if (!d.address.trim())
    out.push({
      field: 'destination.address',
      message: 'Alamat tujuan wajib diisi.',
    });
  const lat = parseQty(d.latitude);
  const lng = parseQty(d.longitude);
  if (lat === null || lat < -90 || lat > 90)
    out.push({
      field: 'destination.latitude',
      message: 'Latitude harus angka -90…90.',
    });
  if (lng === null || lng < -180 || lng > 180)
    out.push({
      field: 'destination.longitude',
      message: 'Longitude harus angka -180…180.',
    });
  return out;
};

const OutboundRequestEditModal: React.FC<Props> = ({
  order,
  onClose,
  onSaved,
}) => {
  const initial = useMemo(() => (order ? draftOf(order) : null), [order]);
  const [draft, setDraft] = useState<DestinationDraft | null>(initial);
  const { hubs } = useWarehouse();
  const [hubID, setHubID] = useState<number | null>(order?.hubID ?? null);
  const [distance, setDistance] = useState<DistanceDraft>(() =>
    distanceDraftOf(order?.distance)
  );
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const server = useServerErrors(fieldKeyOf, SINGLE_STEP);
  const { errors, setErrors } = server;

  useEffect(() => {
    setDraft(initial);
    setHubID(order?.hubID ?? null);
    setDistance(distanceDraftOf(order?.distance));
    setErrors([]);
    setFormError(null);
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, setErrors]);

  const patchDraft = useCallback(
    (patch: Partial<DestinationDraft>) =>
      setDraft((d) => (d ? { ...d, ...patch } : d)),
    []
  );
  const fieldError = useCallback(
    (key: DestinationField) => server.fieldError(key),
    [server]
  );

  if (!order || !draft || !initial) return null;

  // A moved point regenerates the distance unless ops typed one.
  const moved =
    draft.latitude !== initial.latitude ||
    draft.longitude !== initial.longitude;
  const shownDistance =
    moved && !distance.touched ? { value: '', touched: false } : distance;
  // The hub moves only before picking (OPEN) and never on an order a
  // transfer warehouse raised (its source hub is the transfer's).
  const hubEditable = order.status === 'OPEN' && !order.hubTransfer;
  const hubChanged = hubID !== null && hubID !== order.hubID;
  const dirty =
    !sameDraft(draft, initial) ||
    typedDistance(distance) !== undefined ||
    hubChanged;
  const distanceProblem = distanceError(distance);
  const f = order.fulfillment;
  const dashShipment =
    order.status === 'SHIPPED' && f?.carrier === 'DASH' && !!f.shipmentID;
  const manualCarrier =
    order.status === 'SHIPPED' && !!f?.carrier && f.carrier !== 'DASH';

  const submit = async () => {
    const local = localErrors(draft);
    setFormError(null);
    if (local.length || distanceProblem) {
      setErrors(local);
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    setBusy(true);
    setErrors([]);
    try {
      const saved = await warehouseApi.updateOutboundOrder(order.id, {
        // Only when moved: the new hub with its snapshot (name / code).
        ...(hubChanged && hubID !== null ? buildHubSnapshot(hubs, hubID) : {}),
        recipient: {
          name: draft.recipientName.trim(),
          phone: draft.recipientPhone.trim(),
        },
        destination: {
          address: draft.address.trim(),
          latitude: parseQty(draft.latitude),
          longitude: parseQty(draft.longitude),
          notes: draft.destNotes.trim(),
          distance: typedDistance(distance),
          // Sent only when it differs from what is stored (picked, cleared
          // or re-detected); untouched → the server decides.
          ...((draft.city?.id ?? null) !== (initial.city?.id ?? null)
            ? { cityID: draft.city?.id ?? null }
            : {}),
        },
      });
      onSaved(saved);
    } catch (err) {
      const shown = server.fromError(err);
      if (shown.message !== null) setFormError(shown.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      icon="ri-edit-line"
      title="Ubah outbound request"
      subtitle={order.code}
      width="lg"
      confirmOnClose={!busy && dirty}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            onClick={() => void submit()}
            loading={busy}
            leftIcon={<i className="ri-check-line text-[14px]" />}
            data-testid="outbound-edit-submit"
          >
            Simpan
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="outbound-edit-dialog">
        {dashShipment && (
          <Alert
            status="information"
            appearance="stroke"
            size="lg"
            title={`Shipment ${f?.waybill ?? ''} ikut diubah`}
          >
            Penerima, alamat & titik (H3) di shipment dan Dispatch ikut
            berubah — hanya selama belum di-dispatch. Cetak ulang shipping
            label setelah disimpan.
          </Alert>
        )}
        {manualCarrier && (
          <Alert
            status="warning"
            appearance="stroke"
            size="lg"
            title="Resi kurir sudah terbit"
          >
            Perubahan hanya di WMS — kabari kurir dan cetak ulang label bila
            perlu.
          </Alert>
        )}
        {errors.length > 0 && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title={`${errors.length} isian perlu diperbaiki`}
            data-testid="outbound-edit-errors"
          >
            <ul className="mt-1 list-disc pl-4">
              {errors.map((e, i) => (
                <li key={`${i}-${e.message}`}>{e.message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {formError && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title="Gagal menyimpan"
            data-testid="outbound-edit-error"
          >
            {formError}
          </Alert>
        )}
        <FormField
          label="Hub / warehouse"
          htmlFor="oo-edit-hub"
          required
          hint={
            hubEditable
              ? 'Bisa dipindah ke hub WMS lain selama belum picking.'
              : 'Hub hanya bisa diubah sebelum picking (status Open).'
          }
          testId="outbound-edit-hub"
        >
          <HubSelect
            value={hubID}
            onChange={(id) => id !== null && setHubID(id)}
            disabled={!hubEditable || busy}
            exclude={
              order.type === 'TRANSFER' ? [order.destinationHub?.id ?? null] : []
            }
            aria-label="Hub"
          />
          {hubChanged && (
            <p className={metaCls} data-testid="outbound-edit-hub-moved">
              Order pindah dari {order.hub?.name ?? `Hub #${order.hubID}`} —
              picking nanti dikerjakan hub yang baru.
            </p>
          )}
        </FormField>
        <OutboundDestinationStep
          client={
            order.client
              ? { ...order.client, id: order.clientID }
              : { id: order.clientID, name: `Client #${order.clientID}` }
          }
          transfer={order.type === 'TRANSFER'}
          value={draft}
          onChange={patchDraft}
          fieldError={fieldError}
        />
        <DistanceField
          value={shownDistance}
          auto={moved}
          onChange={setDistance}
          error={distanceProblem}
          testId="outbound-edit-distance"
        />
      </div>
    </Modal>
  );
};

export default OutboundRequestEditModal;
